/**
 * Client mirror of App\Services\Ontology\GranularityRules.
 *
 * The builder edits a graph that has not been saved yet, so it cannot ask the
 * server which tiers a depth may carry without a round trip per keystroke. This
 * is the same rule, transliterated, used only to drive the authoring UI — the
 * PHP remains the authority and revalidates everything on write.
 *
 * Two implementations of one rule is how a wrong client-computed
 * `static_lineage` once reached a 500 at passport-create. The guard against a
 * repeat is tests/fixtures/granularity-rules.json: both this module and the PHP
 * are tested against that one file, so a rule that changes on one side only
 * turns the other side red.
 *
 * Keep the two in step by construction — if you change the logic here, change
 * GranularityRules.php in the same commit and add the case to the fixture.
 */

/** Reporting tiers, ordered coarse → fine. Index is the rank. */
export const TIERS = ['model', 'batch', 'item'];

const LABELS = {
    model: 'Model',
    batch: 'Batch',
    item: 'Item',
};

/** Human name for a tier, including the unreported state. */
export function tierLabel(tier) {
    return LABELS[tier] ?? 'Not reported';
}

/** The tier one step finer, or null when `tier` is already the finest. */
function finerThan(tier) {
    return TIERS[TIERS.indexOf(tier) + 1] ?? null;
}

/** The tier a given tier must sit inside, or null for the top tier. */
export function coarserThan(tier) {
    const rank = TIERS.indexOf(tier);

    return rank > 0 ? TIERS[rank - 1] : null;
}

/**
 * Granularity of the nearest ancestor that carries one.
 *
 * Unreported ancestors are transparent: a Model at A is still what C inherits
 * when B between them reports nothing. A value that is not a recognised tier
 * counts as absent, matching the PHP — the server reports it as its own error
 * rather than cascading a second one onto every depth beneath it.
 *
 * The `seen` guard is for graphs that are not trees yet. The canvas can hold a
 * transient cycle while an author is rewiring, and this must not hang on one.
 */
function inheritedTier(graph, depth) {
    const seen = new Set();
    let cursor = graph[depth]?.parent ?? null;

    while (cursor !== null && cursor !== undefined && graph[cursor] && !seen.has(cursor)) {
        seen.add(cursor);

        const tier = graph[cursor].granularity ?? null;
        if (TIERS.includes(tier)) {
            return tier;
        }

        cursor = graph[cursor].parent ?? null;
    }

    return null;
}

/**
 * The depths that OPEN `tier` — carry it without inheriting it.
 *
 * An ontology reports one Model, one Batch and one Item, so a tier may be
 * opened once: two Batches on separate branches would leave an Item with no
 * answer to which Batch it is in. Continuing an inherited run opens nothing and
 * is always fine, however deep it goes.
 */
function openersOf(graph, tier) {
    return Object.keys(graph).filter(
        (id) => (graph[id].granularity ?? null) === tier && inheritedTier(graph, id) !== tier,
    );
}

/**
 * Whether giving `depth` this tier would leave the graph with one run of it.
 *
 * The question is about the RESULTING graph, not the current one. With
 * `A(model) → B(?) → C(batch)`, `C` opens the batch today — but setting `B` to
 * batch makes `B` the opener and leaves `C` merely continuing that run, so the
 * result still has exactly one. Refusing `B` because something already opens a
 * batch would block an author from extending the run upwards, which is the same
 * edit as extending it downwards.
 */
function opensCleanly(graph, depth, tier) {
    const hypothetical = { ...graph, [depth]: { ...graph[depth], granularity: tier } };

    return openersOf(hypothetical, tier).length <= 1;
}

/** The OTHER depth that would still open `tier` alongside `depth`, if any. */
function rivalOpener(graph, depth, tier) {
    const hypothetical = { ...graph, [depth]: { ...graph[depth], granularity: tier } };

    return openersOf(hypothetical, tier).find((id) => id !== depth) ?? null;
}

/**
 * Every granularity `depth` may legally carry, null (unreported) first.
 *
 * `graph` is the stored shape: { A: { type, parent, children, granularity? } }.
 */
export function allowedFor(graph, depth) {
    if (!graph[depth]) {
        return [null];
    }

    const inherited = inheritedTier(graph, depth);

    // Nothing above this depth is reported, so the only tier it can open is the
    // top one — and opening the top tier subdivides nothing, so it is available
    // whatever this depth's type is. That is why a singular root can be a Model.
    if (inherited === null) {
        return opensCleanly(graph, depth, TIERS[0]) ? [null, TIERS[0]] : [null];
    }

    // Staying at the inherited tier continues an existing run and opens nothing.
    const allowed = [null, inherited];
    const next = finerThan(inherited);

    if (next !== null && graph[depth].type !== 'singular' && opensCleanly(graph, depth, next)) {
        allowed.push(next);
    }

    return allowed;
}

/** Whether `candidate` sits anywhere below `ancestor`. */
export function isDescendantOf(graph, candidate, ancestor) {
    return hasAncestorIn(graph, candidate, [ancestor]);
}

/** Whether any of `keys` is an ancestor of `depth`. */
function hasAncestorIn(graph, depth, keys) {
    const seen = new Set();
    let cursor = graph[depth]?.parent ?? null;

    while (cursor !== null && cursor !== undefined && graph[cursor] && !seen.has(cursor)) {
        seen.add(cursor);

        if (keys.includes(cursor)) {
            return true;
        }

        cursor = graph[cursor].parent ?? null;
    }

    return false;
}

/**
 * The depths carrying each tier's reported ID, as the author picked them.
 *
 * Mirror of App\Services\Ontology\GranularityResolver::identities(). Which
 * level of a company's own hierarchy the registry should be given as "the item"
 * is a judgement about that business, not something the shape of the graph
 * settles, so it is chosen rather than derived.
 *
 * @returns {{model: string[], batch: string[], item: string[]}}
 */
export function identitiesFor(graph) {
    const identities = Object.fromEntries(TIERS.map((tier) => [tier, []]));

    for (const id of Object.keys(graph ?? {})) {
        const tier = graph[id].granularity ?? null;

        if (graph[id].is_core && TIERS.includes(tier)) {
            identities[tier].push(id);
        }
    }

    return identities;
}

/**
 * The depth already carrying `tier`'s id, if it is not `depth` itself.
 *
 * A tier is named by exactly one depth, so picking a second means turning the
 * first off. Reported rather than silently moved: switching the core changes
 * which passport the registry is given, which is the author's call to make
 * deliberately.
 */
export function coreHeldBy(graph, depth, tier) {
    return Object.keys(graph ?? {}).find(
        (id) => id !== depth && graph[id].is_core && (graph[id].granularity ?? null) === tier,
    ) ?? null;
}

/**
 * Why `depth` cannot be the core, or null when it can.
 *
 * Any depth carrying a tier may be picked — but only one per tier, so an
 * existing pick has to be released first.
 */
export function reasonCoreUnavailable(graph, depth) {
    const tier = graph[depth]?.granularity ?? null;

    if (!TIERS.includes(tier)) {
        return 'Give this depth a granularity first — a core names the tier it carries.';
    }

    return coreHeldBy(graph, depth, tier) !== null
        ? `An ontology reports one ${tier} id, and another depth already carries it. Turn it off there first.`
        : null;
}

/**
 * The `series` depths of the same tier sitting below `depth`.
 *
 * A series under the core holds many instances per core, so its values cannot be
 * reported as part of the one entity the core names — there is no single value
 * to give. Worth saying plainly; it is not an error, because the author may well
 * mean the coarser thing to be the reported one.
 */
export function seriesBelow(graph, depth) {
    const tier = graph[depth]?.granularity ?? null;

    return Object.keys(graph ?? {}).filter(
        (id) => id !== depth
            && graph[id].type === 'series'
            && (graph[id].granularity ?? null) === tier
            && hasAncestorIn(graph, id, [depth]),
    );
}

/**
 * Tiers whose run is not yet named by one reachable core.
 *
 * Either nothing is picked, or the pick sits down one branch of a forking run
 * and the others would report nothing. Both stop a graph being published or a
 * template completed.
 */
export function tiersMissingACore(graph) {
    return TIERS.filter((tier) => {
        const inTier = Object.keys(graph ?? {}).filter((id) => (graph[id].granularity ?? null) === tier);

        if (inTier.length === 0) {
            return false;
        }

        // A leaf of the run ends a path through it, so checking every leaf
        // checks every path.
        return inTier.some((id) => {
            const hasChildInRun = (graph[id].children ?? []).some((child) => inTier.includes(child));

            if (hasChildInRun) {
                return false;
            }

            return !graph[id].is_core
                && !inTier.some((other) => graph[other].is_core && hasAncestorIn(graph, id, [other]));
        });
    });
}

/** Slack between a hull's edge and the nodes it encloses, in canvas units. */
export const HULL_PADDING = 14;

/** Slack between a cut-out and the excluded depth it frees from a hull. */
export const HOLE_PADDING = 10;

/**
 * Rule-shaped graph from the canvas's own nodes and edges.
 *
 * The three canvases that draw an ontology all hold React Flow state rather
 * than a stored graph, and the builder's is not even saved yet — its ids are
 * temporary until the server assigns depth letters. Everything downstream wants
 * { id: { type, parent, children, granularity } }, so build it in one place
 * rather than three slightly different ways.
 *
 * `addEntity` nodes are the add-a-child affordance, not depths, and are skipped
 * — the same sentinel OntologyDraftService drops on the way in.
 */
export function graphFromFlow(nodes, edges) {
    const graph = {};

    for (const node of nodes ?? []) {
        if (node.type === 'addEntity') {
            continue;
        }

        graph[node.id] = {
            type: node.data?.type ?? null,
            granularity: node.data?.granularity ?? null,
            is_core: Boolean(node.data?.is_core),
            // Carried whole rather than picked over: the data-element rule reads
            // each property's id, name and mapping, and a field missing from
            // here is a field every client-side rule silently cannot see.
            properties: Array.isArray(node.data?.properties) ? node.data.properties : [],
            parent: null,
            children: [],
        };
    }

    for (const edge of edges ?? []) {
        if (!graph[edge.source] || !graph[edge.target]) {
            continue;
        }

        graph[edge.source].children.push(edge.target);
        graph[edge.target].parent = edge.source;
    }

    return graph;
}

/** Box of a laid-out React Flow node, or null before the canvas measures it. */
function boxOf(node) {
    const width = node?.measured?.width ?? node?.width ?? null;
    const height = node?.measured?.height ?? node?.height ?? null;

    if (!width || !height || !node.position) {
        return null;
    }

    return { x: node.position.x, y: node.position.y, width, height };
}

/**
 * The overlap of two boxes, or null when they do not touch.
 *
 * A hole is CLIPPED to its hull rather than merely tested against it. The hull
 * is drawn as one even-odd path, where a point's crossing count decides whether
 * it is filled — so the part of a hole hanging outside the outer subpath counts
 * one crossing, comes out odd, and renders as a filled tab jutting off the side
 * of the hull instead of as a cut-out.
 */
function intersection(a, b) {
    const x = Math.max(a.x, b.x);
    const y = Math.max(a.y, b.y);
    const right = Math.min(a.x + a.width, b.x + b.width);
    const bottom = Math.min(a.y + a.height, b.y + b.height);

    if (right <= x || bottom <= y) {
        return null;
    }

    return { x, y, width: right - x, height: bottom - y };
}

/**
 * The nearest reported depth above `id`, stepping over unreported ones.
 *
 * Same transparency {@see allowedFor} inherits through: a depth that reports to
 * nobody does not interrupt the run around it.
 */
function reportedParent(graph, id) {
    const seen = new Set();
    let cursor = graph[id]?.parent ?? null;

    while (cursor && graph[cursor] && !seen.has(cursor)) {
        seen.add(cursor);

        if (TIERS.includes(graph[cursor].granularity ?? null)) {
            return cursor;
        }

        cursor = graph[cursor].parent ?? null;
    }

    return null;
}

/** The nearest reported depths below `id`, stepping over unreported ones. */
function reportedChildren(graph, id, out = [], seen = new Set()) {
    for (const child of graph[id]?.children ?? []) {
        if (seen.has(child) || !graph[child]) {
            continue;
        }

        seen.add(child);

        if (TIERS.includes(graph[child].granularity ?? null)) {
            out.push(child);
        } else {
            reportedChildren(graph, child, out, seen);
        }
    }

    return out;
}

/**
 * One hull per tier run, ready to draw.
 *
 * A run is a maximal same-tier set connected through paths whose intermediate
 * depths are all unreported — the same transparency the rule inherits through.
 * With `A(model) → B(unreported) → C(model)` and `A → D(model)`, all three
 * reported depths are one Model and belong in one group; splitting on `B` would
 * strand `C` in a group of its own.
 *
 * Every depth the hull encloses but does not own is cut out of it. A cut runs
 * out to the hull's edge on any side with no member beyond it, so a depth in a
 * corner is bitten out rather than left ringed by hull — but never when that
 * would sever the region, because a group is one thing and should read as one.
 * `C` above sits below `B` with nothing to `B`'s left, and extending that cut
 * leftwards would cut `C` adrift.
 *
 * A depth the canvas has not measured yet is skipped rather than drawn at zero
 * size, which would flash a hull in the corner on first paint.
 *
 * @returns {{tier: string, depths: string[], holes: {x: number, y: number, width: number, height: number}[], x: number, y: number, width: number, height: number}[]}
 */
export function regionsFor(graph, nodes) {
    const boxes = new Map();
    for (const node of nodes ?? []) {
        const box = boxOf(node);
        if (box) {
            boxes.set(node.id, box);
        }
    }

    const regions = [];
    const assigned = new Set();

    for (const depth of Object.keys(graph ?? {})) {
        const tier = graph[depth]?.granularity ?? null;

        if (!TIERS.includes(tier) || assigned.has(depth)) {
            continue;
        }

        const members = [];
        const queue = [depth];
        assigned.add(depth);

        while (queue.length > 0) {
            const current = queue.shift();
            members.push(current);

            for (const neighbour of [reportedParent(graph, current), ...reportedChildren(graph, current)]) {
                if (neighbour && !assigned.has(neighbour) && (graph[neighbour].granularity ?? null) === tier) {
                    assigned.add(neighbour);
                    queue.push(neighbour);
                }
            }
        }

        const measured = members.filter((id) => boxes.has(id));
        if (measured.length === 0) {
            continue;
        }

        const hull = hullAround(measured.map((id) => boxes.get(id)));

        regions.push({
            tier,
            depths: members,
            holes: cutsFrom(graph, boxes, hull, tier, measured.map((id) => boxes.get(id))),
            ...hull,
        });
    }

    return regions;
}

/** The padded bounding box of a set of node boxes. */
function hullAround(boxes) {
    const left = Math.min(...boxes.map((b) => b.x));
    const top = Math.min(...boxes.map((b) => b.y));
    const right = Math.max(...boxes.map((b) => b.x + b.width));
    const bottom = Math.max(...boxes.map((b) => b.y + b.height));

    return {
        x: left - HULL_PADDING,
        y: top - HULL_PADDING,
        width: right - left + HULL_PADDING * 2,
        height: bottom - top + HULL_PADDING * 2,
    };
}

/**
 * The cut-outs for every depth a hull encloses but does not own.
 *
 * Each cut starts as the excluded depth's padded box and is then pushed out to
 * the hull's edge on each side that has no member beyond it — but only while
 * the region stays in one piece. Extending a cut that has members on both sides
 * of it would slice the group in two, which is why each extension is applied
 * only if the silhouette count is unchanged by it.
 */
function cutsFrom(graph, boxes, hull, tier, memberBoxes) {
    const cuts = [];

    for (const [id, box] of boxes) {
        // A same-tier depth is never cut out, even one from a run the hull only
        // happens to overlap: it belongs to this grouping either way.
        if (!graph[id] || (graph[id].granularity ?? null) === tier) {
            continue;
        }

        const padded = intersection(hull, {
            x: box.x - HOLE_PADDING,
            y: box.y - HOLE_PADDING,
            width: box.width + HOLE_PADDING * 2,
            height: box.height + HOLE_PADDING * 2,
        });

        if (padded) {
            cuts.push({ cut: padded, box });
        }
    }

    const accepted = cuts.map(({ cut }) => cut);
    const pieces = (candidates) => hullRings({ ...hull, holes: candidates }).outer.length;
    const intact = pieces(accepted);

    cuts.forEach(({ box }, index) => {
        const centreX = box.x + box.width / 2;
        const centreY = box.y + box.height / 2;
        const beyond = (axis, sign) =>
            memberBoxes.some((member) =>
                axis === 'x'
                    ? Math.sign(member.x + member.width / 2 - centreX) === sign
                    : Math.sign(member.y + member.height / 2 - centreY) === sign);

        for (const side of ['left', 'right', 'top', 'bottom']) {
            const axis = side === 'left' || side === 'right' ? 'x' : 'y';
            const sign = side === 'left' || side === 'top' ? -1 : 1;

            if (beyond(axis, sign)) {
                continue;
            }

            const current = accepted[index];
            const stretched = intersection(hull, {
                x: side === 'left' ? hull.x : current.x,
                y: side === 'top' ? hull.y : current.y,
                width: side === 'left'
                    ? current.x + current.width - hull.x
                    : (side === 'right' ? hull.x + hull.width - current.x : current.width),
                height: side === 'top'
                    ? current.y + current.height - hull.y
                    : (side === 'bottom' ? hull.y + hull.height - current.y : current.height),
            });

            if (!stretched) {
                continue;
            }

            const candidates = accepted.slice();
            candidates[index] = stretched;

            if (pieces(candidates) === intact) {
                accepted[index] = stretched;
            }
        }
    });

    return accepted;
}

/**
 * Why a tier is unavailable on a depth, for the disabled option's help text.
 * Returns null when the tier is allowed.
 */
export function reasonTierUnavailable(graph, depth, tier) {
    if (allowedFor(graph, depth).includes(tier)) {
        return null;
    }

    const inherited = inheritedTier(graph, depth);
    const requires = coarserThan(tier);

    const rival = inherited === tier ? null : rivalOpener(graph, depth, tier);
    if (rival) {
        return `An ontology reports one ${tier}, and depth ${rival} would still start its own. Only a depth on the same branch can share it.`;
    }

    if (inherited === null) {
        return `A ${tier} must sit inside a ${requires}, and no depth above this one is reported.`;
    }

    const rank = TIERS.indexOf(tier);
    const inheritedRank = TIERS.indexOf(inherited);

    if (rank < inheritedRank) {
        return `This depth sits inside a ${inherited}. Granularity can only stay the same or get finer as you go down.`;
    }

    if (rank > inheritedRank + 1) {
        return `A ${tier} must sit inside a ${requires}, but the nearest reported depth above this one is a ${inherited}.`;
    }

    return `A singular depth is one-to-one with its parent, so there would be exactly one ${tier} per ${inherited}. Only a series or many depth can open a finer tier.`;
}

/**
 * The real outline of a hull with its cut-outs removed.
 *
 * The earlier approach — one even-odd path with a rectangle per cut — drew a
 * border all the way around every cut, so an excluded depth ended up ringed by
 * the very group it had been cut from, and the notches met the hull's edge in
 * hard right angles. Both are artefacts of drawing the cuts as shapes in their
 * own right rather than as absences.
 *
 * So the region is resolved into its actual boundary instead. The hull and its
 * cuts are all axis-aligned, so their edges induce a small grid — at most a few
 * cells per cut. A cell is solid when it is not inside any cut; the boundary is
 * every side where a solid cell meets a gap; chaining those sides gives closed
 * rings. An outer ring is the shape's own silhouette. A ring around a cut the
 * region fully surrounds is a hole, and comes back separately so the caller can
 * fill it away without stroking it.
 *
 * @returns {{outer: {x: number, y: number}[][], holes: {x: number, y: number}[][]}}
 */
export function hullRings(region) {
    const right = region.x + region.width;
    const bottom = region.y + region.height;

    const axis = (lo, hi, values) => [...new Set([lo, hi, ...values].map((v) => Math.round(v * 1e3) / 1e3))]
        .filter((v) => v >= lo && v <= hi)
        .sort((a, b) => a - b);

    const xs = axis(region.x, right, region.holes.flatMap((h) => [h.x, h.x + h.width]));
    const ys = axis(region.y, bottom, region.holes.flatMap((h) => [h.y, h.y + h.height]));

    const solid = (i, j) => {
        if (i < 0 || j < 0 || i >= xs.length - 1 || j >= ys.length - 1) {
            return false;
        }

        const cx = (xs[i] + xs[i + 1]) / 2;
        const cy = (ys[j] + ys[j + 1]) / 2;

        return !region.holes.some((h) => cx > h.x && cx < h.x + h.width && cy > h.y && cy < h.y + h.height);
    };

    // Sides are emitted so the solid cell is always on the same hand, which is
    // what makes the chained rings consistently wound and lets a hole be told
    // from a silhouette by the sign of its area alone.
    const edges = new Map();
    const key = (p) => `${p.x},${p.y}`;
    const addEdge = (from, to) => {
        if (!edges.has(key(from))) {
            edges.set(key(from), []);
        }
        edges.get(key(from)).push(to);
    };

    for (let i = 0; i < xs.length - 1; i++) {
        for (let j = 0; j < ys.length - 1; j++) {
            if (!solid(i, j)) {
                continue;
            }

            const tl = { x: xs[i], y: ys[j] };
            const tr = { x: xs[i + 1], y: ys[j] };
            const br = { x: xs[i + 1], y: ys[j + 1] };
            const bl = { x: xs[i], y: ys[j + 1] };

            if (!solid(i, j - 1)) addEdge(tl, tr);
            if (!solid(i + 1, j)) addEdge(tr, br);
            if (!solid(i, j + 1)) addEdge(br, bl);
            if (!solid(i - 1, j)) addEdge(bl, tl);
        }
    }

    const rings = [];

    for (const [startKey, ends] of edges) {
        while (ends.length > 0) {
            const start = { x: Number(startKey.split(',')[0]), y: Number(startKey.split(',')[1]) };
            const ring = [start];
            let current = ends.shift();

            while (key(current) !== startKey) {
                ring.push(current);

                const next = edges.get(key(current));
                if (!next || next.length === 0) {
                    break;
                }

                current = next.shift();
            }

            if (ring.length >= 4) {
                rings.push(collapseCollinear(ring));
            }
        }
    }

    const outer = [];
    const holes = [];
    for (const ring of rings) {
        (signedArea(ring) > 0 ? outer : holes).push(ring);
    }

    return { outer, holes };
}

/** Drop the mid-points the grid introduces along a straight run. */
function collapseCollinear(ring) {
    const out = [];

    for (let i = 0; i < ring.length; i++) {
        const prev = ring[(i - 1 + ring.length) % ring.length];
        const point = ring[i];
        const next = ring[(i + 1) % ring.length];

        const straight = (prev.x === point.x && point.x === next.x) || (prev.y === point.y && point.y === next.y);
        if (!straight) {
            out.push(point);
        }
    }

    return out;
}

function signedArea(ring) {
    let sum = 0;

    for (let i = 0; i < ring.length; i++) {
        const a = ring[i];
        const b = ring[(i + 1) % ring.length];
        sum += a.x * b.y - b.x * a.y;
    }

    return sum / 2;
}

/**
 * A closed ring as an SVG subpath with every corner rounded.
 *
 * Convex and concave corners alike: the arc's sweep follows the turn, so a
 * notch bitten into the hull curves out of it as smoothly as the outside
 * corners curve into it. The radius is capped at half the shorter neighbouring
 * run, so a short segment rounds off rather than overshooting into its
 * neighbour.
 */
export function ringPath(ring, radius) {
    if (ring.length < 3) {
        return '';
    }

    const segments = [];

    for (let i = 0; i < ring.length; i++) {
        const prev = ring[(i - 1 + ring.length) % ring.length];
        const point = ring[i];
        const next = ring[(i + 1) % ring.length];

        const inLength = Math.hypot(point.x - prev.x, point.y - prev.y);
        const outLength = Math.hypot(next.x - point.x, next.y - point.y);
        const r = Math.max(0, Math.min(radius, inLength / 2, outLength / 2));

        const inUnit = { x: (point.x - prev.x) / (inLength || 1), y: (point.y - prev.y) / (inLength || 1) };
        const outUnit = { x: (next.x - point.x) / (outLength || 1), y: (next.y - point.y) / (outLength || 1) };

        const from = { x: point.x - inUnit.x * r, y: point.y - inUnit.y * r };
        const to = { x: point.x + outUnit.x * r, y: point.y + outUnit.y * r };
        const sweep = inUnit.x * outUnit.y - inUnit.y * outUnit.x > 0 ? 1 : 0;

        segments.push({ from, to, r, sweep });
    }

    let d = `M${round(segments[0].from.x)},${round(segments[0].from.y)}`;

    for (let i = 0; i < segments.length; i++) {
        const { to, r, sweep } = segments[i];
        const nextFrom = segments[(i + 1) % segments.length].from;

        if (r > 0) {
            d += ` A${round(r)},${round(r)} 0 0 ${sweep} ${round(to.x)},${round(to.y)}`;
        }

        d += ` L${round(nextFrom.x)},${round(nextFrom.y)}`;
    }

    return `${d} Z`;
}

function round(value) {
    return Math.round(value * 100) / 100;
}

/**
 * Fill and stroke for one region.
 *
 * They differ deliberately: the fill takes the holes too, so an enclosed cut
 * shows through as a gap, while the stroke takes only the silhouette — a
 * border ringing an excluded depth would make it look like a member of the very
 * group it was cut out of.
 */
export function hullShape(region, radius) {
    const { outer, holes } = hullRings(region);

    return {
        fill: [...outer, ...holes].map((ring) => ringPath(ring, radius)).join(' '),
        stroke: outer.map((ring) => ringPath(ring, radius)).join(' '),
    };
}
